import {
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Tooltip,
} from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { ColumnSeparator, Container, CustomTableCell } from './styles';
import { useManutencaoFiscal } from '~/pages/ManutencaoFiscal/ManutencaoFiscalContext';
import { formatCurrencyAsText } from '~/utils/functions';

interface TotalizerConfig {
  /**
   * Oculta a exibição do totalizador da coluna.
   * A lógica de soma continua sendo calculada normalmente.
   */
  hide?: boolean;
}

export interface CustomGridColumns {
  field: string;
  headerName: string;
  width: number;
  minWidth?: number;
  align?: string;
  fixed?: boolean;
  hide?: boolean;
  sortable?: boolean;
  disableColumnMenu?: boolean;
  renderCell?: (row: any) => void;
  fixedDirection?: string;
  /**
   * Configuração do totalizador (linha de soma).
   * Use `totalizer.hide = true` para ocultar o totalizador de uma coluna específica.
   */
  totalizer?: TotalizerConfig;
}

interface FormProps {
  rows: any[];
  columns: CustomGridColumns[];
  stickyMenuBackground?: string;
}

const TOTALIZER_BG = '#f5f5f5';
const HIGHLIGHT_BG = 'rgba(136, 80, 191, 0.12)';

export const CustomDataGrid: React.FC<FormProps> = ({
  rows,
  columns,
  stickyMenuBackground = '',
}) => {
  const { loadingFiscal } = useManutencaoFiscal();

  const bodyScrollRef = useRef<HTMLDivElement | null>(null);
  const footerScrollRef = useRef<HTMLDivElement | null>(null);
  const isSyncingScrollRef = useRef(false);

  const visibleColumns = useMemo(
    () => columns.filter((column) => !column.hide),
    [columns],
  );

  const tableWidth = useMemo(
    () => visibleColumns.reduce((acc, col) => acc + (col.width || 0), 0),
    [visibleColumns],
  );

  const HIGHLIGHT_FIELDS = useMemo(
    () => new Set<string>(['val_icms_st', 'val_ipi']),
    [],
  );

  const parseNumber = (value: unknown): number => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number' && Number.isFinite(value)) return value;

    const raw = String(value).trim();
    if (!raw) return 0;

    const cleaned = raw.replace(/[^0-9,.-]/g, '');
    if (!cleaned) return 0;

    const hasComma = cleaned.includes(',');
    const hasDot = cleaned.includes('.');

    let normalized = cleaned;
    if (hasComma && hasDot) {
      normalized = cleaned.replace(/\./g, '').replace(/,/g, '.');
    } else if (hasComma && !hasDot) {
      normalized = cleaned.replace(/,/g, '.');
    } else if (!hasComma && hasDot) {
      const isThousands = /^-?\d{1,3}(\.\d{3})+$/.test(cleaned);
      if (isThousands) normalized = cleaned.replace(/\./g, '');
    }

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatQuantityAsText = (value: number) => {
    const nf = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 6,
    });

    return nf.format(value);
  };

  const totalizer = useMemo(() => {
    const startIndex = visibleColumns.findIndex(
      (c) =>
        c.field === 'qtd_embalagem' ||
        c.headerName?.toLowerCase() === 'embalagem',
    );
    const endIndex = visibleColumns.findIndex(
      (c) => c.field === 'val_ipi' || c.headerName?.toLowerCase() === 'ipi',
    );

    if (startIndex < 0 || endIndex < 0 || endIndex < startIndex) {
      return {
        startIndex: -1,
        endIndex: -1,
        totalsByField: {} as Record<string, number>,
        summableFields: new Set<string>(),
      };
    }

    const inRangeColumns = visibleColumns.slice(startIndex, endIndex + 1);
    const isLikelySummable = (field: string) => /^(qtd_|val_)/.test(field);

    const summableFields = new Set<string>();
    inRangeColumns.forEach((col) => {
      const hasAnyNumeric = rows.some((r) => parseNumber(r?.[col.field]) !== 0);
      if (isLikelySummable(col.field) || hasAnyNumeric || rows.length === 0) {
        summableFields.add(col.field);
      }
    });

    const totalsByField: Record<string, number> = {};
    summableFields.forEach((field) => {
      totalsByField[field] = rows.reduce(
        (acc, r) => acc + parseNumber(r?.[field]),
        0,
      );
    });

    return { startIndex, endIndex, totalsByField, summableFields };
  }, [rows, visibleColumns]);

  const renderColGroup = () => (
    <colgroup>
      {visibleColumns.map((col) => (
        <col
          key={`col-${col.field}`}
          style={{
            width: col.width,
            minWidth: col.width,
            maxWidth: col.width,
          }}
        />
      ))}
    </colgroup>
  );

  const createCell = (column: CustomGridColumns, row: any) => {
    const cellContent = column.renderCell
      ? column.renderCell(row)
      : row[column.field];

    const highlightClassName = HIGHLIGHT_FIELDS.has(column.field)
      ? 'highlightCell'
      : '';

    const baseStyle: React.CSSProperties = {
      width: column.width,
      minWidth: column.width,
      maxWidth: column.width,
      textAlign: column.align as any,
    };

    if (column.fixed) {
      return (
        <Tooltip
          title={`Seq: ${row.num_item} - ${row.des_produto} - Código do Produto: ${row.cod_produto}`}
          placement="bottom"
        >
          <CustomTableCell
            key={column.field}
            background={stickyMenuBackground}
            columnWidth={column.width}
            className={highlightClassName}
            style={{
              ...baseStyle,
              position: 'sticky',
              right: 0,
              textAlign: 'center',
            }}
          >
            {cellContent}
          </CustomTableCell>
        </Tooltip>
      );
    }

    return (
      <Tooltip
        title={`Seq: ${row.num_item} - ${row.des_produto} - Código do Produto: ${row.cod_produto}`}
        placement="bottom"
      >
        <TableCell
          key={column.field}
          className={highlightClassName}
          style={baseStyle}
        >
          {cellContent}
        </TableCell>
      </Tooltip>
    );
  };

  const formatTotalValue = (column: CustomGridColumns, value: number) => {
    if (/^qtd_/.test(column.field)) return formatQuantityAsText(value);

    if (typeof column.renderCell === 'function') {
      const totalRow = { [column.field]: value };
      return column.renderCell(totalRow);
    }

    return formatCurrencyAsText(value);
  };

  const createTotalCell = (column: CustomGridColumns, index: number) => {
    const isInRange =
      totalizer.startIndex >= 0 &&
      index >= totalizer.startIndex &&
      index <= totalizer.endIndex;

    const isSummable = isInRange && totalizer.summableFields.has(column.field);
    const shouldHideTotalizer = !!column.totalizer?.hide;

    const isHighlighted = HIGHLIGHT_FIELDS.has(column.field);
    const highlightClassName = isHighlighted ? 'highlightCell' : '';

    const content = (() => {
      if (!isSummable || index === 0) return '';
      if (shouldHideTotalizer) return '';
      const total = totalizer.totalsByField[column.field] ?? 0;
      return formatTotalValue(column, total);
    })();

    const baseStyle: React.CSSProperties = {
      width: column.width,
      minWidth: column.width,
      maxWidth: column.width,
      textAlign: column.align as any,
      zIndex: column.fixed ? 4 : 1,
    };

    const cellBackground = isHighlighted ? HIGHLIGHT_BG : TOTALIZER_BG;

    if (column.fixed) {
      return (
        <CustomTableCell
          key={`total-${column.field}`}
          background={cellBackground}
          columnWidth={column.width}
          className={`totalizerCell ${highlightClassName}`}
          style={{
            ...baseStyle,
            position: 'sticky',
            right: 0,
            textAlign: 'center',
          }}
        >
          {content}
        </CustomTableCell>
      );
    }

    return (
      <TableCell
        key={`total-${column.field}`}
        className={`totalizerCell ${highlightClassName}`}
        style={{
          ...baseStyle,
          background: cellBackground,
        }}
      >
        {content}
      </TableCell>
    );
  };

  const syncScrollLeft = useCallback(
    (
      from: React.RefObject<HTMLDivElement>,
      to: React.RefObject<HTMLDivElement>,
    ) => {
      if (!from.current || !to.current) return;
      if (isSyncingScrollRef.current) return;

      isSyncingScrollRef.current = true;
      to.current.scrollLeft = from.current.scrollLeft;

      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    },
    [],
  );

  const handleBodyScroll = useCallback(() => {
    syncScrollLeft(bodyScrollRef, footerScrollRef);
  }, [syncScrollLeft]);

  const handleFooterScroll = useCallback(() => {
    syncScrollLeft(footerScrollRef, bodyScrollRef);
  }, [syncScrollLeft]);

  useEffect(() => {
    handleBodyScroll();
  }, [handleBodyScroll, tableWidth]);

  return (
    <Container>
      <Paper
        style={{
          width: '100%',
          maxHeight: '35rem',
          height: '28rem',
          flex: 1,
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <LinearProgress
          className={loadingFiscal ? '' : 'disabled-linear-progress'}
          style={{
            width: '100%',
            top: '60px',
            height: '5px',
            margin: 'auto',
            zIndex: 4,
            background: loadingFiscal ? '#c2a0f8' : 'transparent',
            color: loadingFiscal ? '#8850BF' : 'transparent',
          }}
        />

        <TableContainer
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div
            ref={bodyScrollRef}
            onScroll={handleBodyScroll}
            className="gridScrollBody"
          >
            <Table style={{ tableLayout: 'fixed', width: tableWidth }}>
              {renderColGroup()}

              <TableHead className="headerPosition">
                <TableRow>
                  {visibleColumns.map((column) => (
                    <TableCell
                      key={column.field}
                      className="text-truncate"
                      style={{
                        position: 'sticky',
                        top: 0,
                        right: column.fixed ? 0 : undefined,
                        width: column.width,
                        minWidth: column.width,
                        maxWidth: column.width,
                        background: column.fixed
                          ? '#57069e'
                          : HIGHLIGHT_FIELDS.has(column.field)
                          ? 'rgba(136, 80, 191, 0.15)'
                          : '#fff',
                        zIndex: column.fixed ? 30 : 20,
                        textAlign: column.fixed ? 'center' : undefined,
                      }}
                    >
                      {column.headerName}

                      {!column.fixed && (
                        <ColumnSeparator>
                          <svg
                            className="MuiSvgIcon-root MuiDataGrid-iconSeparator"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path d="M11 19V5h2v14z" />
                          </svg>
                        </ColumnSeparator>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={
                      row.num_item ?? `${row.cod_produto}-${row.des_produto}`
                    }
                    style={{ borderColor: '#8850BF' }}
                  >
                    {visibleColumns.map((column) => createCell(column, row))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div
            ref={footerScrollRef}
            className="gridScrollFooter"
            onScroll={handleFooterScroll}
            aria-label="Rolagem horizontal da grade"
          >
            <Table style={{ tableLayout: 'fixed', width: tableWidth }}>
              {renderColGroup()}
              <TableFooter>
                <TableRow className="totalizerRow" role="row" tabIndex={-1}>
                  {visibleColumns.map((column, index) =>
                    createTotalCell(column, index),
                  )}
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        </TableContainer>
      </Paper>
    </Container>
  );
};
